import React, { useState } from "react";
import PrimaryFolder from "../Components/PrimaryFolder";
import "../FileRepo.css";
import SecondaryFolder from "../Components/SecondaryFolder";
import DataTable, { RowActionsTray } from "../Components/DataTable";
import { useAuth } from "../../../features/auth/AuthContext";
import { toast } from "react-toastify";
import RenameModal from "../FileActions/RenameModal";
import DeleteModal from "../FileActions/DeleteModal";
import MoveFile from "../FileActions/MoveFile";
import Loader from "../Components/Loader";
import { format } from "date-fns";
import { fileIcons } from "../Icons";
import { formatFileSize } from "../utils/utils";
import { getFileActions } from "../utils/fileActions";
import { useFileActions } from "../hooks/useFileActions";
import { useModal } from "../hooks/useModal";
import { useRecentAccessed, useHomeCounts } from "../hooks/useHomeQuery";
import NoFiles from "../Components/EmptyList/NoFiles";
import FilePreviewModal2 from "../../FilePreview/FilePreviewModal2";
import Filelist from "../Components/Mobile/Filelist";

const Home = ({ folderClick }) => {
  const { token } = useAuth();
  const { modal, reqData, openModal, closeModal } = useModal();

  const {
    data: recentData,
    isLoading: recentLoading,
    isFetching: recentFetching,
  } = useRecentAccessed(token);

  const {
    data: countsData,
    isLoading: countsLoading,
    isFetching: countsFetching,
  } = useHomeCounts(token);

  const {
    favourite,
    download,
    openRename,
    openDelete,
    openMove,
    preview,
    deleteFile,
    isDeleting,
    isFavouriting,
  } = useFileActions({ token, openModal, closeModal });

  /* ---------------- COMBINED STATES ---------------- */

  const isLoading = recentLoading || countsLoading;
  const isFetching = recentFetching || countsFetching;

  const recentFolders = recentData?.recent_folders || [];
  const recentFiles = recentData?.recent_files || [];

  const courseCount = countsData?.courseCount || 0;
  const profileCount = countsData?.profileCount || 0;

  async function handleSuccess(message) {
    toast.success(message);
    closeModal();
  }

  const handlers = {
    favourite,
    openRename,
    openDelete,
    openMove,
    download,
  };

  const columns = [
    {
      key: "s3_url",
      title: "Name",
      sortable: true,
      sortAccessor: (row) =>
        row?.item_type === "folder" ? row?.folder_name : row?.file_name,
      render: (val, row) => {
        const FileIcon = fileIcons?.[row?.file_type] || fileIcons?.folder;

        return row?.item_type === "folder" ? (
          <button
            onClick={() => folderClick(row)}
            className="border-none bg-transparent flex items-center gap-2 w-full"
          >
            <span>{FileIcon && <FileIcon />}</span>
            <span
              title={row?.folder_name}
              className="truncate text-[#555555] text-[0.8rem]"
            >
              {row?.folder_name}
            </span>
          </button>
        ) : (
          <button
            className="border-none bg-transparent flex items-center gap-2 w-full"
            onClick={() => preview(row)}
          >
            <span>{FileIcon && <FileIcon />}</span>
            <span
              title={row?.file_name}
              className="truncate text-[#555555] text-[0.8rem]"
            >
              {row?.file_name}
            </span>
          </button>
        );
      },
    },
    {
      key: "upload_type",
      title: "",
      render: (val) => <div className="file-type">{val}</div>,
    },
    {
      key: "full_path",
      title: "Location",
      align: "left",
      render: (val) => (
        <div title={val} className="truncate w-[95%]">
          {val?.replace(/^Manually uploaded\//i, "")}
        </div>
      ),
    },
    {
      key: "updated_at",
      title: "Updated On",
      align: "left",
      sortable: true,
      render: (val) => (
        <div className="text-[#555555] text-[0.8rem]">
          {format(new Date(val), "dd/MM/yy")}
        </div>
      ),
    },
    {
      key: "file_size",
      title: "Size",
      align: "left",
      sortable: true,
      render: (val, row) => (
        <span className="text-[#555555] text-[0.8rem]">
          {formatFileSize(row?.file_size)}
        </span>
      ),
    },
    {
      key: "actions",
      title: "",
      render: (val, row, idx, isHovered) => {
        const rowActions = getFileActions({ row, handlers });

        return (
          // <div
          //   style={{
          //     position: "relative",
          //     width:"80%",
          //     padding: "0 8px",
          //   }}
          // >
          <RowActionsTray actions={rowActions} row={row} visible={isHovered} />
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-[1.2rem]">
      <div className="head-text">File Storage Overview</div>

      {/* ---------------- PRIMARY FOLDERS ---------------- */}

      <div className="flex gap-[0.8rem] flex-col sm:flex-row">
        <PrimaryFolder
          label="Courses"
          value={courseCount}
          handleClick={() => folderClick({ id: 1, name: "Courses" })}
          icon={
            <svg
              width="27"
              height="22"
              viewBox="0 0 27 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.66667 21.3333C1.93333 21.3333 1.30556 21.0722 0.783333 20.55C0.261111 20.0278 0 19.4 0 18.6667V2.66667C0 1.93333 0.261111 1.30556 0.783333 0.783333C1.30556 0.261111 1.93333 0 2.66667 0H10.6667L13.3333 2.66667H24C24.7333 2.66667 25.3611 2.92778 25.8833 3.45C26.4056 3.97222 26.6667 4.6 26.6667 5.33333V18.6667C26.6667 19.4 26.4056 20.0278 25.8833 20.55C25.3611 21.0722 24.7333 21.3333 24 21.3333H2.66667Z"
                fill="#555555"
              />
            </svg>
          }
        />

        <PrimaryFolder
          label="My Profile"
          value={profileCount}
          handleClick={() => folderClick({ id: 2, name: "My Profile" })}
          icon={
            <svg
              width="27"
              height="22"
              viewBox="0 0 27 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.66667 21.3333C1.93333 21.3333 1.30556 21.0722 0.783333 20.55C0.261111 20.0278 0 19.4 0 18.6667V2.66667C0 1.93333 0.261111 1.30556 0.783333 0.783333C1.30556 0.261111 1.93333 0 2.66667 0H10.6667L13.3333 2.66667H24C24.7333 2.66667 25.3611 2.92778 25.8833 3.45C26.4056 3.97222 26.6667 4.6 26.6667 5.33333V18.6667C26.6667 19.4 26.4056 20.0278 25.8833 20.55C25.3611 21.0722 24.7333 21.3333 24 21.3333H2.66667ZM12 17.3333H22.6667V16.6C22.6667 15.6 22.1778 14.8056 21.2 14.2167C20.2222 13.6278 18.9333 13.3333 17.3333 13.3333C15.7333 13.3333 14.4444 13.6278 13.4667 14.2167C12.4889 14.8056 12 15.6 12 16.6V17.3333ZM19.2167 11.2167C19.7389 10.6944 20 10.0667 20 9.33333C20 8.6 19.7389 7.97222 19.2167 7.45C18.6944 6.92778 18.0667 6.66667 17.3333 6.66667C16.6 6.66667 15.9722 6.92778 15.45 7.45C14.9278 7.97222 14.6667 8.6 14.6667 9.33333C14.6667 10.0667 14.9278 10.6944 15.45 11.2167C15.9722 11.7389 16.6 12 17.3333 12C18.0667 12 18.6944 11.7389 19.2167 11.2167Z"
                fill="#555555"
              />
            </svg>
          }
        />
      </div>

      {isLoading || isFavouriting ? (
        <div className=" bg-white rounded-[12px] flex h-full items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          {/* ---------------- RECENT FOLDERS ---------------- */}

          <div className="md:shadow-[0_0_12px_#00000014] p-2 md:!p-8 bg-white rounded-[12px] flex flex-col gap-[1.2rem]">
            <div className="primary-folder-label">Recent Folders</div>

            {recentFolders?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {recentFolders.map((folder) => (
                  <SecondaryFolder
                    folderClick={folderClick}
                    key={folder?.id}
                    folder={folder}
                    label={folder?.folder_name}
                    isFav={folder?.is_favorite}
                    changeFav={favourite}
                  />
                ))}
              </div>
            ) : (
              <NoFiles
                primaryLabel="No Folders Found"
                secondaryLabel="Folders will appear here once they are added"
              />
            )}
          </div>
          {/* ---------------- RECENT FILES ---------------- */}

          <div className="md:shadow-[0_0_12px_#00000014] p-2 md:!p-8 bg-white rounded-[12px] flex flex-col gap-[1.2rem]">
            <div className="primary-folder-label">Recent Files</div>
            {window.innerWidth < 640 ? (
              <Filelist
                data={recentFiles}
                folderClick={folderClick}
                filePreview={preview}
                handleMove={openMove}
                handleRename={openRename}
                handleDelete={openDelete}
                changeFav={favourite}
                setSelectedKeys={null}
              />
            ) : (
              <DataTable
                columns={columns}
                dataSource={recentFiles}
                rowKey="id"
                emptyText="No Files found"
              />
            )}
          </div>
        </>
      )}

      {/* ---------------- MODALS ---------------- */}

      <RenameModal
        open={modal === "rename"}
        close={closeModal}
        file={reqData}
      />

      <DeleteModal
        open={modal === "delete"}
        close={closeModal}
        handleDelete={() => deleteFile(reqData, "soft")}
        fileName={reqData?.file_name}
        loading={isDeleting}
      />

      <FilePreviewModal2
        isOpen={modal === "filePreview" ? true : false}
        file={reqData}
        onClose={closeModal}
      />

      <MoveFile open={modal === "moveFile"} file={reqData} close={closeModal} />
    </div>
  );
};

export default Home;
